/**
 * Browser-side parser for Trans Dist DO CSV files.
 *
 * Extracts metadata (distributor, region, period) from the filename,
 * reads the semicolon-separated CSV in-browser to count data rows,
 * and sums the "Netto" column while converting trailing minus formats
 * (e.g. 3568014- -> -3568014) to floats.
 */

export type TransDistDoPreviewResult = {
  distributorName: string
  regionName: string
  periodYear: number
  periodMonth: number
  rowCount: number
  valueNetSum: number
}

// Regex to extract: distributor_name, region_name, period_year, period_month
// Pattern: {distributor_name}, {region_name} - {YYYY}-{MM}-Week {W}
const FILENAME_REGEX = /^(.+?),\s*(.+?)\s*-\s*(\d{4})-(\d{2})-Week\s*\d+/i

export async function parseTransDistDoForPreview(file: File): Promise<TransDistDoPreviewResult> {
  const filename = file.name
  const match = filename.match(FILENAME_REGEX)
  if (!match) {
    throw new Error(
      "Nama file tidak valid. Format harus: '{Distributor Name}, {Region Name} - {YYYY}-{MM}-Week {W} ...'"
    )
  }

  const distributorName = match[1].trim()
  const regionName = match[2].trim()
  const periodYear = parseInt(match[3], 10)
  const periodMonth = parseInt(match[4], 10)

  if (isNaN(periodMonth) || periodMonth < 1 || periodMonth > 12 || isNaN(periodYear) || periodYear <= 1900) {
    throw new Error(`Periode tidak valid pada "${filename}".`)
  }

  const text = await file.text()
  const lines = text.split(/\r?\n/)
  if (lines.length === 0 || !lines[0].trim()) {
    throw new Error("File CSV kosong.")
  }

  const headers = parseCsvLine(lines[0])
  const nettoIdx = headers.findIndex((h) => h.trim().toLowerCase() === "netto")
  if (nettoIdx === -1) {
    throw new Error("Kolom 'Netto' tidak ditemukan dalam file.")
  }

  let rowCount = 0
  let valueNetSum = 0

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    const row = parseCsvLine(line)
    if (row.length <= nettoIdx) continue

    const rawNetto = row[nettoIdx]
    const val = parseSapDecimal(rawNetto)
    if (val !== null) {
      valueNetSum += val
    }
    rowCount++
  }

  return {
    distributorName,
    regionName,
    periodYear,
    periodMonth,
    rowCount,
    valueNetSum: Math.round(valueNetSum * 10000) / 10000,
  }
}

/**
 * Splits a CSV line by semicolon, respecting fields enclosed in double quotes.
 */
function parseCsvLine(line: string): string[] {
  const result: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ";" && !inQuotes) {
      result.push(current)
      current = ""
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

/**
 * Normalizes and parses SAP decimal values, e.g. "3568014-" -> -3568014
 */
function parseSapDecimal(val: string | null | undefined): number | null {
  if (!val) return null
  const valStr = val.replace(/[,\s]/g, "")
  if (!valStr) return null

  if (valStr.endsWith("-")) {
    const num = parseFloat(valStr.slice(0, -1))
    return isNaN(num) ? null : -num
  } else {
    const num = parseFloat(valStr)
    return isNaN(num) ? null : num
  }
}
